import { useState, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import OtpInput from 'react-otp-input';

import useI18n from '../../hooks/use-i18n';
import { otpModalHeading } from '../../utils/flightpath/config';
import Button from '../flight-path/button';

import '../../styles/flight-path/otp_modal.scss';

export default function OtpInputField(props) {
  const {
    otpType,
    error,
    values,
    setRetryError,
    retryError,
    resendOtp,
    onSubmit,
    sectionChange,
    isSubmitOtpLoading,
    sourceType
  } = props;

  const i18n = useI18n();
  const loginPage = i18n.t('login_page');
  const tpvTranslations = i18n.t('flight_path.treatment_plan_view');
  const intervalRef = useRef();
  const intervalRefRetry = useRef();

  const [resendCounter, setResendCounter] = useState(120);
  const [retryCounter, setRetryCounter] = useState(59);
  const [otp, setOtp] = useState('');

  const resendOtpClick = () => {
    if (resendCounter !== 0) return;
    clearInterval(intervalRef.current);
    setResendCounter(120);
    resendOtp();
    intervalRef.current = setInterval(decreaseNum, 1000);
  };

  const decreaseNum = () => {
    setResendCounter((prev) => prev - 1);
  };

  const decreaseNumRetry = () => {
    setRetryCounter((prev) => prev - 1);
  };

  const getOtpSendInfo = () => (otpType === 'EMAIL' ? loginPage.sent_otp_mobile : loginPage.sent_otp_email);

  useEffect(() => {
    intervalRef.current = setInterval(decreaseNum, 1000);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(intervalRefRetry.current);
    };
  }, []);

  useEffect(() => {
    if (error) setOtp('');
  }, [error]);

  useEffect(() => {
    if (resendCounter === 0) {
      clearInterval(intervalRef.current);
    }
  }, [resendCounter]);

  useEffect(() => {
    if (retryCounter === 0) {
      clearInterval(intervalRefRetry.current);
      setRetryError('');
    }
  }, [retryCounter]);

  useEffect(() => {
    if (retryError) {
      setRetryCounter(59);
      intervalRefRetry.current = setInterval(decreaseNumRetry, 1000);
    }
  }, [retryError]);

  return (
    <div className="d-flex flex-column align-items-center">
      <h3 className="mt-3 otp-heading">{i18n.t(otpModalHeading[sourceType])}</h3>
      {sourceType === 'TREATMENT_PLAN' && <p>{tpvTranslations.otp_message}</p>}
      <h5 className="opt-sent-info">
        <span>{loginPage.otp_sent}</span>
        <br />
        <strong>
          {otpType === 'EMAIL'
            ? values.email
            : `${values.isdCode} ${values.phoneNumber?.replace(
              /^.{6}/g,
              '******'
            )}`}
        </strong>
      </h5>
      <div className={`otp-wrapper ${error ? 'error' : ''}`}>
        <OtpInput
          value={otp}
          onChange={(val) => setOtp(val)}
          containerStyle="otp-container"
          numInputs={5}
          isInputNum={true}
          inputStyle="otp-input"
          renderInput={(inputs) => <input {...inputs} />}
          inputType="tel"
          separator={<span>&nbsp;</span>}
        />
        <span className="text-error">{error}</span>
      </div>
      <h6 className="opt-receive-info">
        {loginPage.otp_msg}
        &nbsp;
        <span
          role="presentation"
          onClick={resendOtpClick}
          className={resendCounter !== 0 ? 'disabled' : 'enabled'}
        >
          {loginPage.resend}
          &nbsp;(
          {Math.floor(resendCounter / 60)}
          :
          {resendCounter % 60}
          )
        </span>
      </h6>
      <Button
        onClick={() => onSubmit(otp)}
        isLoading={isSubmitOtpLoading}
        disabled={retryError}
      >
        {loginPage.submit}
      </Button>
      <a
        className="d-block mt-5 otp-change"
        role="presentation"
        onClick={() => { sectionChange(false); setResendCounter(120); }}
      >
        {sourceType === 'LOGIN' ? loginPage.change_otp : getOtpSendInfo()}
      </a>
      {retryError && (
        <span className="text-error mt-2">{`${retryError} ${retryCounter} ${loginPage.seconds}`}</span>
      )}
    </div>
  );
}

OtpInputField.propTypes = {
  otpType: PropTypes.string,
  error: PropTypes.string,
  values: PropTypes.shape(),
  setRetryError: PropTypes.func,
  sectionChange: PropTypes.func,
  retryError: PropTypes.string,
  onSubmit: PropTypes.func,
  resendOtp: PropTypes.func,
  isSubmitOtpLoading: PropTypes.bool,
  sourceType: PropTypes.string
};

OtpInputField.defaultProps = {
  otpType: '',
  error: '',
  values: {},
  setRetryError: () => {},
  retryError: '',
  onSubmit: () => {},
  sectionChange: () => {},
  resendOtp: () => {},
  isSubmitOtpLoading: false,
  sourceType: 'LOGIN'
};
